import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { QRAnalyticsService } from '../../../services/qrAnalyticsService';
import { QRSoftDeleteService } from '../../../services/qrSoftDeleteService';
import { useAuth } from '../../../contexts/AuthContext';

export function QRCodeList({
  qrCodes = [],
  isLoading = false,
  error = '',
  onSelectQR,
  selectedQRs = [],
  onShowDetails,
  onDelete,
  onPermanentDelete,
  onUpdate,
  isTrashView = false
}) {
  const { user } = useAuth();
  
  // NEW: Delete confirmation state management - same as grid view
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Handle QR code deletion with confirmation - same as grid view
  const handleDeleteQR = async (qrCode) => {
    if (!qrCode?.id) return;
    
    setDeleteConfirm({
      qrCode,
      show: true
    });
  };

  // Confirm delete action - Soft delete or permanent delete based on isTrashView
  const confirmDelete = async () => {
    if (!deleteConfirm?.qrCode?.id || !user?.id) return;
    
    setDeleteLoading(true);
    setDeleteError('');
    
    try {
      console.log('🗑️ Attempting to delete QR code:', {
        id: deleteConfirm?.qrCode?.id,
        name: deleteConfirm?.qrCode?.name,
        userId: user?.id,
        permanent: isTrashView
      });
      
      let result;
      
      if (isTrashView) {
        // Permanent delete from trash
        result = await QRSoftDeleteService.bulkPermanentlyDeleteQRCodes([deleteConfirm?.qrCode?.id], user.id);
      } else {
        // Soft delete (move to trash)
        result = await QRSoftDeleteService.bulkSoftDeleteQRCodes([deleteConfirm?.qrCode?.id], user.id);
      }
      
      console.log('🗑️ Delete result:', result);
      
      // Check if the operation failed completely or if this specific QR code failed
      if (!result?.success || (result?.successful === 0 && result?.failed > 0)) {
        // Show specific error message to user
        const errorMsg = result?.successful === 0 && result?.failed > 0
          ? `Cannot delete "${deleteConfirm?.qrCode?.name}": ${isTrashView ? 'Error performing permanent delete' : "You don't own this QR code or it's already deleted"}` 
          : `Delete failed: ${result?.error || 'Unknown error'}`;
        alert(errorMsg);
        setDeleteConfirm(null); // Close modal
        return;
      }
      
      // Note: onDelete and onPermanentDelete callbacks removed to prevent double deletion
      
      // Clear any previous errors on successful delete
      setDeleteError('');
      
      // Success feedback
      console.log(`✅ QR code "${deleteConfirm?.qrCode?.name}" ${isTrashView ? 'permanently deleted' : 'moved to trash'} successfully`);
      
    } catch (error) {
      console.error('❌ Error deleting QR code:', error);
      alert(`Failed to delete QR code: ${error?.message || 'Unknown error'}`);
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(null);
    }
  };

  // Cancel delete action
  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString)?.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatScans = (scans) => {
    const scanCount = scans || 0;
    if (scanCount >= 1000000) {
      return `${(scanCount / 1000000)?.toFixed(1)}M`;
    } else if (scanCount >= 1000) {
      return `${(scanCount / 1000)?.toFixed(1)}K`;
    }
    return scanCount?.toString() || '0';
  };

  const getQRTypeLabel = (type) => {
    const typeLabels = {
      url: 'URL',
      text: 'Text',
      email: 'Email',
      phone: 'Phone',
      sms: 'SMS',
      wifi: 'WiFi',
      vcard: 'Contact',
      whatsapp: 'WhatsApp'
    };
    return typeLabels?.[type] || type?.toUpperCase() || 'Unknown';
  };

  const getStatusColor = (qr) => {
    if (!qr?.isValid) return 'text-red-600 bg-red-50';
    if (qr?.expiryDate && new Date(qr?.expiryDate) < new Date()) {
      return 'text-orange-600 bg-orange-50';
    }
    return 'text-green-600 bg-green-50';
  };

  const getStatusText = (qr) => {
    if (!qr?.isValid) return 'Inactive';
    if (qr?.expiryDate && new Date(qr?.expiryDate) < new Date()) {
      return 'Expired';
    }
    return 'Active';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">QR Codes</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {[...Array(5)]?.map((_, i) => (
            <div key={i} className="px-6 py-4 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="w-12 h-12 bg-gray-200 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-6 bg-gray-200 rounded"></div>
                  <div className="w-12 h-6 bg-gray-200 rounded"></div>
                  <div className="w-8 h-8 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-center">
          <Icon name="AlertTriangle" size={48} className="text-red-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load QR Codes</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <Button onClick={() => window.location?.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (qrCodes?.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-center">
          <Icon name="QrCode" size={48} className="text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No QR Codes</h3>
          <p className="text-gray-500 mb-4">Create your first QR code to get started.</p>
          <Button onClick={() => window.location.href = '/qr-code-generator'}>
            Create QR Code
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">QR Codes</h3>
            <span className="text-sm text-gray-500">
              {qrCodes?.length} {qrCodes?.length === 1 ? 'code' : 'codes'}
            </span>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {qrCodes?.map((qr) => (
            <div key={qr?.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                {/* Left section - Checkbox, QR preview, and basic info */}
                <div className="flex items-center space-x-4 flex-1 min-w-0">
                  {/* Selection checkbox */}
                  <div className="flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={selectedQRs?.includes(qr?.id)}
                      onChange={(e) => onSelectQR?.(qr?.id, e?.target?.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>

                  {/* QR Code mini preview */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                      <Icon name="QrCode" size={20} className="text-gray-400" />
                    </div>
                  </div>

                  {/* QR Code info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {qr?.name || 'Untitled QR Code'}
                      </h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(qr)}`}>
                        {getStatusText(qr)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      <span>{getQRTypeLabel(qr?.qr_type)}</span>
                      <span>•</span>
                      <span className="truncate max-w-xs">
                        {qr?.content || qr?.trackableUrl || 'No content'}
                      </span>
                      {qr?.campaignName && (
                        <>
                          <span>•</span>
                          <span className="text-blue-600 font-medium">
                            {qr?.campaignName}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right section - Stats and actions */}
                <div className="flex items-center space-x-6">
                  {/* Scans */}
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {formatScans(qr?.currentScans)}
                    </div>
                    <div className="text-xs text-gray-500">scans</div>
                  </div>

                  {/* Created date */}
                  <div className="text-right hidden sm:block">
                    <div className="text-sm text-gray-500">
                      {formatDate(qr?.createdAt)}
                    </div>
                    <div className="text-xs text-gray-400">created</div>
                  </div>

                  {/* Status indicator */}
                  <div className="flex-shrink-0">
                    <div className={`w-2 h-2 rounded-full ${
                      qr?.isValid ? 'bg-green-400' : 'bg-red-400'
                    }`}></div>
                  </div>

                  {/* Actions dropdown */}
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onShowDetails?.(qr)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteQR(qr)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Copy link functionality
                        if (qr?.trackableUrl) {
                          navigator?.clipboard?.writeText(qr?.trackableUrl);
                        }
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Icon name="Copy" size={16} />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Expandable details section (can be added later) */}
              {qr?.showDetails && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Last Scan:</span>
                      <div>{formatDate(qr?.lastScan) || 'Never'}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Scan Limit:</span>
                      <div>{qr?.scanLimit || 'Unlimited'}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Expires:</span>
                      <div>{formatDate(qr?.expiryDate) || 'Never'}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Short URL:</span>
                      <div className="font-mono text-xs truncate">
                        {qr?.shortUrl || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* NEW: Enhanced Error Display - Show delete-specific errors (same as grid) */}
      {deleteError && (
        <div className="mb-6 p-4 border border-red-200 rounded-lg bg-red-50">
          <div className="flex items-center">
            <Icon name="AlertTriangle" size={20} className="text-red-600 mr-3" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{deleteError}</p>
            </div>
            <button
              onClick={() => setDeleteError('')}
              className="text-red-400 hover:text-red-600"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* NEW: Delete Confirmation Modal - Enhanced with same messaging as grid view */}
      {deleteConfirm?.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Icon name="Trash2" size={20} className="text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Move to Trash</h3>
                <p className="text-sm text-gray-500">You can restore this later</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                Move{' '}
                <span className="font-semibold">"{deleteConfirm?.qrCode?.name || 'this QR code'}"</span>{' '}
                to trash?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This QR code will be moved to trash. You can restore it anytime from the trash.
              </p>
              <p className="text-xs text-blue-600 mt-2 font-medium">
                💡 The QR code will continue working until permanently deleted from trash.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={cancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Icon name="Trash2" size={16} className="mr-2" />
                    Move to Trash
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QRCodeList;